import type { Coordinate, Drawing } from '@orgsim/shared';
import L from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { Polygon, Polyline, useMap, useMapEvents } from 'react-leaflet';
import { Icon } from '../ui/Icon';
import { fromLatLng, toLatLng } from './crs';

type Tool = 'line' | 'polygon' | 'text';
const COLORS = ['#ff5252', '#ffd740', '#69f0ae', '#40c4ff', '#ffffff'];

type Props = {
  onDrawing: (drawing: Drawing) => void;
  drawingActive?: (active: boolean) => void;
  onActivate?: () => void;
};

export function DrawTools({ onDrawing, drawingActive, onActivate }: Props) {
  const map = useMap();
  const [tool, setTool] = useState<Tool | null>(null);
  const [color, setColor] = useState(COLORS[0] ?? '#ff5252');
  const [points, setPoints] = useState<Coordinate[]>([]);
  const [textAt, setTextAt] = useState<Coordinate | null>(null);
  const [text, setText] = useState('');
  const barRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (barRef.current) L.DomEvent.disableClickPropagation(barRef.current);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setTool(null);
        setPoints([]);
        setTextAt(null);
        setText('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Focus the text field once it appears, without the noAutofocus-flagged JSX attribute.
  useEffect(() => {
    if (textAt) textInputRef.current?.focus();
  }, [textAt]);

  useEffect(() => {
    drawingActive?.(tool !== null);
    // Dragging and double-click zoom would otherwise interfere with placing points.
    if (tool) {
      map.dragging.disable();
      map.doubleClickZoom.disable();
    } else {
      map.dragging.enable();
      map.doubleClickZoom.enable();
    }
    // Re-enable on unmount too, so the map is never left permanently non-interactive
    // if DrawTools is unmounted (e.g. a read-only view) while a tool is active.
    return () => {
      map.dragging.enable();
      map.doubleClickZoom.enable();
    };
  }, [tool, map, drawingActive]);

  // Shared by reset() and selectTool() so in-progress points and text can never
  // drift apart between the two paths that clear them.
  const clearProgress = () => {
    setPoints([]);
    setTextAt(null);
    setText('');
  };

  const reset = () => {
    setTool(null);
    clearProgress();
  };

  const finish = () => {
    if (tool === 'line' && points.length >= 2) {
      onDrawing({ id: crypto.randomUUID(), kind: 'line', points, color });
    } else if (tool === 'polygon' && points.length >= 3) {
      onDrawing({ id: crypto.randomUUID(), kind: 'polygon', points, color });
    }
    reset();
  };

  useMapEvents({
    click(e) {
      // No tool active: let the click pass through to MapView's own handler (e.g. sign placement).
      if (!tool) return;
      const p = fromLatLng(e.latlng);
      if (tool === 'text') setTextAt(p);
      else setPoints((prev) => [...prev, p]);
    },
    dblclick() {
      if (tool && tool !== 'text') finish();
    },
    keydown(e) {
      if (e.originalEvent.key === 'Escape') reset();
      if (e.originalEvent.key === 'Enter' && tool && tool !== 'text') finish();
    },
  });

  const submitText = () => {
    if (textAt && text.trim()) {
      onDrawing({
        id: crypto.randomUUID(),
        kind: 'text',
        position: textAt,
        text: text.trim(),
        color,
      });
    }
    reset();
  };

  const selectTool = (t: Tool) => {
    if (tool === t) {
      reset();
    } else {
      onActivate?.();
      setTool(t);
      clearProgress();
    }
  };

  const btn = (t: Tool, label: string) => (
    <button
      type="button"
      aria-pressed={tool === t}
      onClick={() => selectTool(t)}
      className={`rounded px-2 py-1 text-sm ${tool === t ? 'bg-red-600 text-white' : 'bg-neutral-800 hover:bg-neutral-700'}`}
    >
      <Icon name={t} size={15} />
      {label}
    </button>
  );

  return (
    <>
      <div ref={barRef} className="leaflet-top leaflet-left draw-tools">
        {/* .leaflet-control (not the Tailwind pointer-events-auto utility, which loses to
            Leaflet's unlayered CSS regardless of import order) restores clicks through
            the pointer-events: none on .leaflet-top/.leaflet-left. */}
        <div className="leaflet-control draw-toolbar">
          {btn('line', 'Linie')}
          {btn('polygon', 'Fläche')}
          {btn('text', 'Text')}
          {tool ? (
            <div className="draw-colors">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Farbe ${c}`}
                  aria-pressed={color === c}
                  onClick={() => setColor(c)}
                  className={`border-2 ${color === c ? 'border-white' : 'border-transparent'}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          ) : null}
          {tool && tool !== 'text' ? (
            <div className="draw-instructions">
              <span>
                {points.length} Punkte · {tool === 'line' ? 'Mindestens 2' : 'Mindestens 3'} setzen
              </span>
              <button
                type="button"
                className="button"
                onClick={finish}
                disabled={points.length < (tool === 'line' ? 2 : 3)}
              >
                Fertig
              </button>
              <button
                type="button"
                className="button quiet"
                onClick={reset}
                aria-label="Zeichnen abbrechen"
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ) : null}
          {tool === 'text' && !textAt ? (
            <div className="draw-instructions">
              <span>Textposition auf der Karte wählen.</span>
              <button
                type="button"
                className="button quiet"
                onClick={reset}
                aria-label="Zeichnen abbrechen"
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ) : null}
          {textAt ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitText();
              }}
              className="draw-instructions"
            >
              <input
                ref={textInputRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Text"
                aria-label="Kartentext"
                className="rounded bg-neutral-800 px-2 py-1 text-sm"
              />
              <button type="submit" className="rounded bg-red-600 px-2 text-sm text-black">
                OK
              </button>
            </form>
          ) : null}
        </div>
      </div>
      {tool === 'line' && points.length > 0 ? (
        <Polyline positions={points.map(toLatLng)} pathOptions={{ color, dashArray: '4 4' }} />
      ) : null}
      {tool === 'polygon' && points.length > 0 ? (
        <Polygon
          positions={points.map(toLatLng)}
          pathOptions={{ color, dashArray: '4 4', fillOpacity: 0.1 }}
        />
      ) : null}
    </>
  );
}
